import api from 'api';
import config from 'config';
import { useDispatch, useReduxState } from 'hooks';
import { actions } from 'store';
import { sendErrorInfoToApi } from 'lib/api';

import type {
  BlockType,
  Course,
  DiscussionComment,
  Reaction,
  Task
} from 'shared-types';

const useApiHandlers = () => {
  const { dispatch } = useDispatch();

  const {
    messages,
    pageState: {
      chapterData,
      courseSlug,
      languageVersion,
      previewLanguage,
      readerCourseData,
      tracker
    },
    serverConfig,
    user
  } = useReduxState();

  const onError = async (err: Error) => await dispatch.handleErrorDispatch(err);

  const sendCustomClientError = (
    blockId: number,
    blockType: string,
    message: string,
    info = ''
  ) => {
    sendErrorInfoToApi({
      error: {
        name: `CustomClientError - Reader - ${blockType}`,
        message,
        stack:
          `BlockId=${blockId}\nUserId=${user?.id}` + (info ? `\n${info}` : '')
      },
      statusCode: 400,
      user: { id: user?.id, roles: user?.roles }
    });
  };

  return {
    getDiscussionComments: async (args: {
      blockId: number;
      fromId?: number;
      languageVersion?: Course['language'];
    }) => {
      try {
        const response = await api.get(
          `/reader/courses/${courseSlug}/discussion-comments` +
            `?blockId=${args.blockId}` +
            (args.fromId ? `&idGreaterThan=${args.fromId}` : '') +
            (args.languageVersion
              ? `&languageVersion=${args.languageVersion}`
              : '')
        );
        if (!response.data || !response.data.success) {
          throw new Error('error');
        }
        return response.data.comments;
      } catch (err) {
        return null;
      }
    },
    getEmbedUrl: (
      blockId: number,
      type: 'embedcode' | 'embedlink' | 'h5p' | 'scorm'
    ) => {
      return (
        config.apiUrl +
        `/reader/courses/${courseSlug}` +
        (type === 'scorm'
          ? `/embed-block-scorm/`
          : type === 'h5p'
          ? `/embed-block-h5p/`
          : `/embed-block-html/`) +
        `${blockId}?language=${languageVersion}` +
        `${previewLanguage ? '&preview=true' : ''}`
      );
    },
    onAnswerRemoved: async (answerId: number, blockId: number) => {
      if (!readerCourseData) return;

      try {
        await api.delete(
          `/admin/courses/${readerCourseData.course.id}/answers/${answerId}`
        );

        dispatch(
          actions.addNotice({
            text: messages.commonDeleteAnswerSuccess,
            type: 'success'
          })
        );
        return true;
      } catch (err) {
        await onError(err);
        return false;
      }
    },
    onAnswerReviewed: async (args: { answerId: number; review: unknown }) => {
      try {
        const response = await api.post(
          `/reader/courses/${courseSlug}/answer-reviews`,
          { data: args.review, AnswerId: args.answerId }
        );
        if (!response.data || !response.data.success) throw new Error('error');

        dispatch(
          actions.addNotice({
            text: messages.readerOpenQuestionReviewSuccess,
            type: 'success'
          })
        );
        return response.data.review;
      } catch (err) {
        await onError(err);
        return null;
      }
    },
    onAnswerSubmitted: async (args: {
      answers: Object;
      blockId: number;
      includeResponseRates?: boolean;
      type: Task['type'];
    }) => {
      if (!readerCourseData) return;

      try {
        const res = await api.post(`/reader/courses/${courseSlug}/answers`, {
          ...args.answers,
          languageVersion,
          type: args.type,
          BlockId: args.blockId,
          ChapterId: chapterData.id,
          CourseId: readerCourseData.course.id
        });

        const answerData = res.data?.data?.id ? res.data.data : null;
        if (
          args.includeResponseRates &&
          answerData &&
          ((serverConfig.multipleChoiceEnableResponseRates &&
            args.type === 'multiplechoice') ||
            (serverConfig.likertEnableResponseRates && args.type === 'likert'))
        ) {
          const rates = await api.get(
            `/reader/courses/${courseSlug}/answers/${args.blockId}/response-rates` +
              (previewLanguage ? '?preview=true' : '')
          );
          answerData.responseRatesData = rates.data?.responseRatesData || null;
        }

        return answerData;
      } catch (err) {
        await onError(err);
        return false;
      }
    },
    onDiscussionCommentDelete: async (
      commentId: number,
      deletionReason?: string
    ) => {
      try {
        let url = `/reader/courses/${courseSlug}/discussion-comments/${commentId}`;

        if (deletionReason) {
          const params = new URLSearchParams({
            deletionReason: deletionReason
          });
          url += `?${params.toString()}`;
        }

        const response = await api.delete(url);
        if (!response.data || !response.data.success) throw new Error('error');
        return true;
      } catch (err) {
        await onError(err);
        return false;
      }
    },
    onDiscussionCommentEdit: async (
      commentId: number,
      args: Pick<DiscussionComment, 'attachment' | 'attachmentType' | 'text'>
    ) => {
      try {
        const response = await api.put(
          `/reader/courses/${courseSlug}/discussion-comments/${commentId}`,
          args
        );
        if (!response.data || !response.data.success) {
          throw new Error('error');
        }

        dispatch(
          actions.addNotice({
            text: messages.readerDiscussionEditCommentSuccess,
            type: 'success'
          })
        );
        return response.data.comment;
      } catch (err) {
        await onError(err);
        return null;
      }
    },
    onDiscussionCommentSubmit: async (
      args: Pick<
        DiscussionComment,
        'attachment' | 'attachmentType' | 'text'
      > & {
        blockId: number;
        isAnonymous: boolean;
        lastCommentId: number;
        parentCommentId: number;
        courseLanguageVersion?: string;
      }
    ) => {
      try {
        const response = await api.post(
          `/reader/courses/${courseSlug}/discussion-comments`,
          {
            text: args.text,
            isAnonymous: args.isAnonymous,
            lastCommentId: args.lastCommentId,
            BlockId: args.blockId,
            ParentCommentId: args.parentCommentId,
            ...(args.courseLanguageVersion
              ? { courseLanguageVersion: args.courseLanguageVersion }
              : {}),
            ...(args.attachment
              ? {
                  attachment: args.attachment,
                  attachmentType: args.attachmentType
                }
              : {})
          }
        );
        if (!response.data || !response.data.success) {
          throw new Error('error');
        }

        dispatch(
          actions.addNotice({
            text: messages.readerDiscussionAddCommentSuccess,
            type: 'success'
          })
        );
        return true;
      } catch (err) {
        await onError(err);
        return false;
      }
    },
    onUserReaction: async ({
      blockId,
      blockType,
      reactions
    }: {
      blockId: Number;
      blockType: BlockType;
      reactions: Reaction[];
    }) => {
      try {
        const response = await api.post(
          `/reader/courses/${courseSlug}/reactions`,
          { blockId, blockType, reactions }
        );
        return response.data.reactions;
      } catch (err) {
        await onError(err);
        return null;
      }
    },
    onVideoAnalyticsEntry: (
      player,
      { blockId, currentTime, initialTime, steps }
    ) => {
      if (!player) {
        sendCustomClientError(blockId, 'BlockVideo', 'Player not found');
        return;
      }

      if (steps.length === 0) {
        // in case videoLength was not properly set
        sendCustomClientError(
          blockId,
          'BlockVideo',
          'Number of video steps is 0'
        );
        return;
      }

      tracker.trackVideoTick({
        blockId,
        steps,
        tickEnd: currentTime,
        tickStart: initialTime < 1 ? 0 : initialTime
      });
    }
  };
};

export default useApiHandlers;
